import { API_CONFIG, TokenStorage, getAuthHeaders, handleApiError, ApiResponse } from './apiConfig'

// Helper pour fetch avec configuration CORS
function fetchWithCors(url: string, options: RequestInit = {}): Promise<Response> {
  return fetch(url, {
    ...options,
    mode: 'cors',
    credentials: 'include',
    headers: {
      ...API_CONFIG.headers,
      ...options.headers
    }
  })
}

// Interfaces pour les données
export interface RegisterData {
  nom: string
  prenom: string
  telephone: string
  email: string
  salaire: number
  quotiteCessiblePourcent: number
  quotiteCessibleMontant: number
  paysId: number
  collectiviteId: number
  dateNaissance: string
  dateEngagement: string
  tokenFcm?: string
  codeSecurite?: string
}

export interface LoginData {
  telephone?: string
  email?: string
  codeSecurite: string
}

export interface LoginRequestData {
  identifiant: string
  codeSecurite: string
}

export interface VerifyOtpData {
  identifiant: string
  otp: string
}

export interface UserData {
  token: string
  id: number
  nom: string
  prenom: string
  photo?: string
  avatar?: string
  actif: boolean
  codeSecurite: string
  telephone: string
  email: string
  fonction?: string
  is_gerant?: boolean
  salaire?: number
  age?: number
  anciennete?: number
  quotiteCessiblePourcent?: number
  quotiteCessibleMontant?: number
  dateNaissance?: string
  dateEngagement?: string
  paysCode?: string
  paysId?: number
  paysNom?: string
  collectivite_id?: number
  collectivite?: {
    id: number
    nom: string
  }
  tokenFcm?: string
  // Propriétés KYC
  piece_identite_type?: string
  numero_piece?: string
  piece_recto?: string
  piece_verso?: string
  bulletin1?: string
  bulletin2?: string
  bulletin3?: string
}

class AuthService {
  /**
   * Vérifier si un numéro de téléphone existe
   */
  async checkPhone(telephone: string): Promise<ApiResponse<UserData>> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/login/check`, {
        method: 'POST',
        headers: API_CONFIG.headers,
        body: JSON.stringify({ telephone: telephone.replace(/\s/g, '') })
      })

      const data = await response.json()
      
      // Si le téléphone existe et retourne des données utilisateur
      if (data.status === 0 && data.data) {
        TokenStorage.setToken(data.data.token)
        TokenStorage.setUser(data.data)
      }
      
      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Inscription d'un nouvel utilisateur
   */
  async register(userData: RegisterData): Promise<ApiResponse<UserData>> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/register`, {
        method: 'POST',
        headers: API_CONFIG.headers,
        body: JSON.stringify(userData)
      })

      const data = await response.json()
      
      // Sauvegarder le token et les données utilisateur en cas de succès
      if (data.status === 0 && data.data) {
        TokenStorage.setToken(data.data.token)
        TokenStorage.setUser(data.data)
      }
      
      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Étape 1 : vérifier les identifiants et envoyer l'OTP
   */
  async requestLogin(payload: LoginRequestData): Promise<ApiResponse> {
    try {
      const identifier = payload.identifiant.trim()
      const body: Record<string, string> = {
        codeSecurite: payload.codeSecurite.trim()
      }

      if (identifier.includes('@')) {
        body.email = identifier
      } else {
        body.telephone = identifier
      }

      const response = await fetchWithCors(`${API_CONFIG.baseURL}/login/otp/request`, {
        method: 'POST',
        headers: API_CONFIG.headers,
        body: JSON.stringify(body)
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Étape 2 : vérifier l'OTP et finaliser la connexion
   */
  async verifyOtp(payload: VerifyOtpData): Promise<ApiResponse> {
    try {
      const identifier = payload.identifiant.trim()
      const body: Record<string, string> = {
        otp: payload.otp.trim()
      }

      if (identifier.includes('@')) {
        body.email = identifier
      } else {
        body.telephone = identifier
      }

      const response = await fetchWithCors(`${API_CONFIG.baseURL}/login/otp/verify`, {
        method: 'POST',
        headers: API_CONFIG.headers,
        body: JSON.stringify(body)
      })

      const data = await response.json()

      // Gérer le nouveau format avec data.data (production-ready)
      // et l'ancien format avec data directement (compatibilité)
      const responseData = data.data || data
      const token = responseData.token || data.token

      if (data.status === 0 && token) {
        TokenStorage.setToken(token)

        const userData = {
          id: responseData.user_id ?? responseData.id,
          pseudo: responseData.pseudo,
          prenom: responseData.prenom,
          nom: responseData.nom,
          email: responseData.email,
          phone: responseData.phone || responseData.telephone,
          telephone: responseData.phone || responseData.telephone,
          fonction: responseData.fonction,
          is_gerant: responseData.is_gerant,
          collectivite_id: responseData.collectivite_id,
          collectivite: responseData.collectivite,
          pays: responseData.pays,
          token: token
        }
        TokenStorage.setUser(userData)

        console.log('[AuthService] Utilisateur connecté:', {
          id: userData.id,
          nom: userData.nom,
          is_gerant: userData.is_gerant,
          token_length: token?.length
        })
      }

      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  async login(credentials: LoginData): Promise<ApiResponse> {
    const identifier = credentials.email ?? credentials.telephone ?? ''
    return this.verifyOtp({
      identifiant: identifier,
      otp: credentials.codeSecurite
    })
  }

  /**
   * Définir le code PIN de sécurité (après inscription)
   */
  async setPin(codepin: string): Promise<ApiResponse> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/login/pin`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ codepin })
      })

      const data = await response.json()
      
      // Mettre à jour le code de sécurité dans les données utilisateur
      if (data.status === 0) {
        const user = TokenStorage.getUser()
        if (user) {
          user.codeSecurite = codepin
          TokenStorage.setUser(user)
        }
      }
      
      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Envoyer un code OTP par email
   */
  async verifyEmail(): Promise<ApiResponse> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/login/verify/email`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({})
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Envoyer un OTP pour la connexion (sans authentification)
   */
  async sendLoginOtp(username: string): Promise<ApiResponse> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/login/send-otp`, {
        method: 'POST',
        headers: API_CONFIG.headers,
        body: JSON.stringify({ username })
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Vérifier le code OTP reçu par email
   */
  async checkEmail(otp: string): Promise<ApiResponse> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/login/check/email`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ otp })
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Demander la réinitialisation du mot de passe
   */
  async forgotPassword(username: string): Promise<ApiResponse> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/login/forgot`, {
        method: 'POST',
        headers: API_CONFIG.headers,
        body: JSON.stringify({ username })
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Vérifier l'OTP pour la réinitialisation du mot de passe
   */
  async verifyOtpForReset(username: string, otp: string): Promise<ApiResponse<{ resetToken: string }>> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/reset-password/verify-otp`, {
        method: 'POST',
        headers: API_CONFIG.headers,
        body: JSON.stringify({ username, otp })
      })

      const data = await response.json()
      
      // Stocker temporairement le token de reset
      if (data.status === 0 && data.resetToken) {
        sessionStorage.setItem('reset_token', data.resetToken)
      }
      
      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Définir un nouveau code de sécurité avec le token de reset
   */
  async resetPasswordWithToken(codepin: string): Promise<ApiResponse> {
    try {
      const resetToken = sessionStorage.getItem('reset_token')
      
      if (!resetToken) {
        return {
          status: 3,
          message: 'Token de réinitialisation introuvable'
        }
      }

      const headers = {
        ...API_CONFIG.headers,
        'Authorization': `Bearer ${resetToken}`
      }

      const response = await fetchWithCors(`${API_CONFIG.baseURL}/reset-password/set-pin`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ codepin })
      })

      const data = await response.json()
      
      // Nettoyer le token de reset en cas de succès
      if (data.status === 0) {
        sessionStorage.removeItem('reset_token')
      }
      
      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Récupérer le statut KYC
   */
  async getKycStatus(): Promise<ApiResponse<{ kyc: boolean }>> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/kyc/statut`, {
        method: 'GET',
        headers: getAuthHeaders()
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Sauvegarder les documents KYC
   */
  async saveKyc(formData: FormData): Promise<ApiResponse> {
    try {
      const headers = getAuthHeaders()
      // Supprimer Content-Type pour laisser le navigateur le gérer (multipart/form-data)
      delete headers['Content-Type']
      
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/kyc/save`, {
        method: 'POST',
        headers: headers,
        body: formData
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Déconnexion
   */
  async logout(): Promise<ApiResponse> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/logout`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({})
      })

      const data = await response.json()
      
      // Nettoyer le stockage local
      TokenStorage.clear()
      
      return data
    } catch (error) {
      TokenStorage.clear()
      return handleApiError(error)
    }
  }

  /**
   * Vérifier si l'utilisateur est authentifié
   */
  isAuthenticated(): boolean {
    return TokenStorage.getToken() !== null
  }

  /**
   * Récupérer les données de l'utilisateur connecté
   */
  getCurrentUser(): UserData | null {
    return TokenStorage.getUser()
  }

  /**
   * Récupérer le profil complet de l'utilisateur
   */
  async getProfile(): Promise<ApiResponse<UserData>> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/profile`, {
        method: 'GET',
        headers: getAuthHeaders()
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Mettre à jour le profil de l'utilisateur
   */
  async updateProfile(profileData: Partial<UserData>): Promise<ApiResponse> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/profile/update`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(profileData)
      })

      const data = await response.json()
      
      // Mettre à jour les données locales si succès
      if (data.status === 0) {
        const currentUser = this.getCurrentUser()
        if (currentUser) {
          TokenStorage.setUser({ ...currentUser, ...profileData })
        }
      }
      
      return data
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Mettre à jour la photo de profil
   */
  async updatePhoto(photoFile: File): Promise<ApiResponse<{ photo: string }>> {
    try {
      const formData = new FormData()
      formData.append('photo', photoFile)

      const headers = getAuthHeaders()
      // Supprimer Content-Type pour laisser le navigateur le gérer
      delete headers['Content-Type']

      const response = await fetchWithCors(`${API_CONFIG.baseURL}/profile/photo`, {
        method: 'POST',
        headers: headers,
        body: formData
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Récupérer les préférences de l'utilisateur
   */
  async getPreferences(): Promise<ApiResponse<{
    theme: string
    notifications: {
      push: boolean
      email: boolean
      sms: boolean
    }
  }>> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/profile/preferences`, {
        method: 'GET',
        headers: getAuthHeaders()
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }

  /**
   * Mettre à jour les préférences de l'utilisateur
   */
  async updatePreferences(preferences: {
    theme?: string
    notifications?: {
      push?: boolean
      email?: boolean
      sms?: boolean
    }
  }): Promise<ApiResponse> {
    try {
      const response = await fetchWithCors(`${API_CONFIG.baseURL}/profile/preferences`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(preferences)
      })

      return await response.json()
    } catch (error) {
      return handleApiError(error)
    }
  }
}

export default new AuthService()

